import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './EndpointEditor.html?raw';

import _StatefulSection from '../../../moreCore/UI/_StatefulSection/_StatefulSection';
import _uiFrameMixin from '../../../moreCore/UI/Container/uiFrame';
import ScenarioEditor from '../Scenario/ScenarioEditor';
import { StringHelpers } from "../../../more/Helpers";
import { ExpandableSection, ExpandableSectionMarkings } from "../../../moreCore/UI/Container/ExpandableSectionTypes";
import { SelectEntityDialogTypes } from "../../../moreCore/_loadingHelpers/SelectEntityDialog";
import { TabContainer } from "../../../moreCore/_loadingHelpers/Tabbing";
import { Endpoint, HtEndpointScenario, Scenario } from "../ScreenplayTypes";
import StaticTable from '../../../moreCore/UI/StaticTable/StaticTable';
import EntityStore from '../../../moreCore/Tools/EntityStore';
import lang from 'dojo/_base/lang';
import CheckBox from '../../../moreCore/UI/CheckBox/CheckBox';
import GenericDialog from '../../../moreCore/UI/GenericDialog/GenericDialog';
import _GlobalSelectEntityDialogsMixin from '../../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import HintBox from '../../../moreCore/UI/HintBox/HintBox';
import FloatingMenu from '../../../moreCore/UI/Floating/Menu';
import HtEndpointScenarioEditor from './HtEndpointScenarioEditor';
import DateInput from '../../../moreCore/UI/DateInput/DateInput';

class EndpointEditor extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private embeddedEditorContainer: HTMLDivElement;
    private embeddedEditor: MdsLax<InstanceType<typeof ScenarioEditor>>;

    private sectionTableContainer: HTMLDivElement;
    private tableContainer: MdsLax<InstanceType<typeof ExpandableSection>>;
    private store: MdsLax<InstanceType<typeof EntityStore<HtEndpointScenario>>>;
    private table: MdsLax<InstanceType<typeof StaticTable>>;

    private dateStartInput: MdsLax<InstanceType<typeof DateInput>>;

    private inputConfiguration: MdsLax<InstanceType<typeof HintBox>>;
    private inputFallbackTemplate: MdsLax<InstanceType<typeof HintBox>>;

    private buttonNew: HTMLButtonElement;

    private rowConfig: StaticTable.RowConfig = {
        attributes: [
            null,
            //"order",
            "ref_screenplay_scenario.$Displayname",
            "date_start",
            "date_end",
            "is_time_relative_to_first_booking",
            "time_start",
            "time_end"
        ],
        headings: [
            (this as any).translate("General_Label_Edit"),
            //this.translate("General_Label_Order"),
            (this as any).translate("Endpoint_Label_Scenario"),
            (this as any).translate("Endpoint_Label_DateStart"),
            (this as any).translate("Endpoint_Label_DateEnd"),
            (this as any).translate("Endpoint_Label_RelativeoFirstBooking"),
            (this as any).translate("Endpoint_Label_TimeStart"),
            (this as any).translate("Endpoint_Label_TimeEnd")
        ],
        colNamesInternal: [
            "edit",
            //"order",
            "ref_screenplay_scenario.$Displayname",
            "date_start",
            "date_end",
            "is_time_relative_to_first_booking",
            "time_start",
            "time_end"
        ],
        bgColors: [
            null,
            //null,
            null,
            null,
            null,
            null,
            null,
            null
        ],
        colspans: [
            1,
            //1,
            3,
            2,
            2,
            1,
            1,
            1
        ]
    }

    private tableLayout: StaticTable.Layout = {
        name: "table",
        label: "table",
        rows: [(this as any).rowConfig],
    }

    private setupTable() {
        let source = this.getAttributeValue("source");
        let tableLayout;
        if (this.table) this.table.destroy();

        this.store = new EntityStore();
        this.store.on("change", lang.hitch(this, this._onStoreChange));
        this.table = new StaticTable();
        this.table.setLayout(this.tableLayout);
        this.table.addDefaultRowActionButton("rowsorting", null, lang.hitch(this, this._enableCallback));
        this.table.addDefaultRowActionButton("delete", null, lang.hitch(this, this._enableCallback));
        this.table.addCustomRowActionButton("editTime", lang.hitch(this, this._onClickEditTime), "fas fa-clock", null, null, null, lang.hitch(this, this._enableCallback));
        this.table.setFormatter(lang.hitch(this, this._tableFormatter));
        this.table.setupFromStore(this.store);
        this.table.setCyAttribute("EndpointEditorTable");
        this.table.placeAt(this.tableContainer);
    }

    private _tableFormatter(cell: StaticTable.StaticTableCell<HtEndpointScenario>): boolean {
        let name = cell.name;
        let entity = cell.entity;
        let node = cell.tableCellNode;

        switch (name) {
            case "edit":
                node.innerHTML = "";
                let checkbox: MdsLax<InstanceType<typeof CheckBox>> = new CheckBox();
                checkbox.setValue(entity.selected ? true : false, true);
                checkbox.on("change", lang.hitch(this, this._onSelectedCheckbox, entity));
                checkbox.placeAt(node);
                let changedDiffData = this.getAttributeValue("changedDiffData");
                let deletedDiffData = this.getAttributeValue("deletedDiffData");
                if ((changedDiffData && changedDiffData.length > 0) || (deletedDiffData && deletedDiffData.length > 0)) checkbox.setReadonly(true);
                return true;
            case "date_start":
            case "date_end":
                if (cell.value instanceof Date) {
                    node.innerHTML = cell.value.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" });
                }  
                return false;
        }
        return false;
    }


    private _onStoreChange() {
        this._setExpanderMarking();
        this._setNewButtonState();
        let changedDiffData = this.store.getCurrentCompleteList("changed");
        this.setAttributeValue("changedDiffData", changedDiffData.length > 0 ? changedDiffData : null);

        let deletedDiffData = this.store.getCurrentCompleteList("deleted");
        this.setAttributeValue("deletedDiffData", deletedDiffData.length > 0 ? deletedDiffData : null);
    }

    private _enableCallback(entity: HtEndpointScenario, rowGroup: StaticTable.StaticTableRowGroup<HtEndpointScenario>): boolean {
        if (this.embeddedEditor) return false;

        return true;
    }

    private _onClickEditTime(rowGroup: StaticTable.StaticTableRowGroup<HtEndpointScenario>) {
        let entity = rowGroup.entity;
        let editor = new HtEndpointScenarioEditor();
        editor.on("editorConfirmed", lang.hitch(this, this._onEditorConfirmed));
        editor.setup(entity);
    }

    private _onEditorConfirmed(entity: HtEndpointScenario) {
        let clonedEntity = lang.clone(entity);
        this.store.changeEntry(clonedEntity, true);
    }

    private _tableHasContent(): boolean {
        return this.store.getCurrentCompleteList().length > 0;
    }

    private _setExpanderMarking(): void {
        this.tableContainer.setMarking(this._tableHasContent() ? ExpandableSectionMarkings.hasContent : ExpandableSectionMarkings.default);
    }

    private _setNewButtonState(): void {
        let storeDirty = this.store.isDirty();
        this.disableNode(this.buttonNew, storeDirty);
    }

    private _onSelectedCheckbox(entity: HtEndpointScenario, value: boolean) {
        let completeList = this.store.getCurrentCompleteList();
        if (value) {
            entity.selected = value;
            for (let currentEntity of completeList) {
                if (entity.ID !== currentEntity.ID) currentEntity.selected = false;
            }

            this._embedEditor(entity);
        } else {
            entity.selected = value;
            this._closeEditor();
        }
        this.table.updateTable();
    }

    private _embedEditor(entity: HtEndpointScenario, isNew: boolean = false, closeCallback?: Function, saveCallback?: Function) {
        if (this.embeddedEditor) {
            this._closeEditor(entity, isNew, closeCallback, saveCallback);
            return;
        }

        this._resetEmbeddedEditor();
        if (isNew) {
            this._setSelectedEntity(entity);
        }

        if (entity) {
            this.hideNode(this.embeddedEditorContainer, false);
            this.embeddedEditor = new ScenarioEditor();
            this.embeddedEditor.setIsNew(isNew);
            this.embeddedEditor.setReloadAfterSave(false);
            this.embeddedEditor.setup(null, entity.ref_screenplay_scenario, "node", this.embeddedEditorContainer);
            if (isNew) this.embeddedEditor.on("clickedSave", lang.hitch(this, this._setEndpoint));
        } else if ((!entity || entity.ref_screenplay_scenario.is_new) && isNew) {
            this.hideNode(this.embeddedEditorContainer, false);
            this.embeddedEditor = new ScenarioEditor();
            this.embeddedEditor.setIsNew(isNew);
            this.embeddedEditor.setReloadAfterSave(false);
            this.embeddedEditor.setup(null, null, "node", this.embeddedEditorContainer);
            this.embeddedEditor.on("clickedSave", lang.hitch(this, this._setEndpoint));
        }

        this.embeddedEditor.setHtEntity(entity);

        if (this.embeddedEditor) this.embeddedEditor.on("saved", lang.hitch(this, this._onClickReloadList, null));
    }

    private _setEndpoint() {
        if (this.embeddedEditor) this.embeddedEditor.setAttributeValue("refEndpoint", this.getAttributeValue("___root"), true);
    }

    private _onClickNewListEntity(evt: MouseEvent): void {
        let refEntity: Scenario;
        refEntity = {
            ID: StringHelpers.randomID(),
            $Displayname: this.translate("Screenplay_Heading_NewScenario"),
            Classname: "",
            ClassPrintname: "",
            description: "",
            EL_ht_screenplay_scenario_block: [],
            EL_ht_screenplay_scenario_ext_urls: [],
            source: "",
            is_new: true
        }
        let htEntity = this._createNewHtEntity(refEntity);

        this._embedEditor(htEntity, true, null, lang.hitch(this, this._embedEditor, htEntity, true));
    }

    private _createNewHtEntity(refEntity: Scenario): HtEndpointScenario {
        let newHtEntity: HtEndpointScenario = {
            ID: StringHelpers.randomID(),
            $Displayname: "",
            Classname: "",
            ClassPrintname: "",
            order: this.store.getCurrentCompleteList().length,
            ref_screenplay_endpoint: this.getAttributeValue("___root"),
            ref_screenplay_scenario: refEntity,
            date_start: null,
            date_end: null,
            is_time_relative_to_first_booking: false,
            time_end: null,
            time_start: null,
            selected: true
        }
        this.store.addEntry(newHtEntity);
        this.table.updateTable();
        return newHtEntity;
    }

    private _setSelectedEntity(entity: HtEndpointScenario) {

        if (!entity) return;
        this._resetSelectedEntities();
        //Muss die Entity so aus dem store holen irgendwie ist entity argument eine andere Referenz
        let storeEntity = this.store.getEntityById(entity.ID);
        storeEntity.selected = true;
        this.table.updateTable();
    }

    private _resetSelectedEntities() {
        if (!this.store) return;
        let completelist = this.store.getCurrentCompleteList();
        for (let entity of completelist) entity.selected = false;
        this.table.updateTable();
    }

    private _getHtEntity(entity: Scenario): HtEndpointScenario {
        if (!entity) return;

        let completelist = this.store.getCurrentCompleteList();
        for (let htScenario of completelist) {
            if (htScenario.ref_screenplay_scenario) {
                if (htScenario.ref_screenplay_scenario.ID === entity.ID) return htScenario;
            }
        }

        return null
    }

    private _removeEntityFromStore(entity: HtEndpointScenario) {
        this.store.deleteEntry(entity);
    }

    private _closeEditor(entity?: HtEndpointScenario, isNew?: boolean, closeCallback?: Function, saveCallback?: Function) {
        if (this.embeddedEditor && this.embeddedEditor.isNew) this.embeddedEditor.forceSetDirty(true);
        if (this.embeddedEditor && (this.embeddedEditor.isDirty())) {
            let closeFunction = this.embeddedEditor.on("closed", lang.hitch(this, this._afterClose, entity, isNew, closeCallback));
            let saveFunction = this.embeddedEditor.on("saved", lang.hitch(this, this._onClickReloadList, saveCallback));
            this.embeddedEditor.on("canceled", lang.hitch(this, this._onCallbackCancel, closeFunction, saveFunction));
            if (this.embeddedEditor.isNew) this.embeddedEditor.on("notSaved", lang.hitch(this, this._removeEntityFromStore, this.embeddedEditor.getHtEntity()));

            let currentEntity = this.embeddedEditor.getHtEntity();
            this._setSelectedEntity(currentEntity);
        }
        if (this.embeddedEditor && !this.embeddedEditor._onCloseRequest()) {
            return;
        } else {
            this._resetEmbeddedEditor();
            if (entity) this._embedEditor(entity, isNew);
        }
    }

    private _onCallbackCancel(closeFunction: dojo.WatchHandle, saveFunction: dojo.WatchHandle) {
        if (closeFunction) closeFunction.remove();
        if (saveFunction) closeFunction.remove();
    }

    private _afterClose(entity?: HtEndpointScenario, isNew?: boolean, callback?: Function) {
        this._resetEmbeddedEditor();
        if (entity) this._setSelectedEntity(entity);
        else this._resetSelectedEntities();

        if (entity || isNew) this._embedEditor(entity, isNew);

        if (callback) callback();
    }

    _onCloseRequest() {
        let hasChanged = this.isDirty() === true;

        if (hasChanged) {
            let dlgConfirm = new GenericDialog();
            dlgConfirm.show(this.translate("Screenplay_Endpoint_AskSavePendingChanges"),
                this.translate("General_PendingChanges") + " - " + this.title,
                "",
                "",
                "yesnocancel",
                lang.hitch(this, this._confirmCallback));

            return false;

        } else {
            return true;
        }
    }

    private _confirmCallback(result) {
        if (result == -1) { // nein
            this.close();
            this.emit("notSaved");
        } else if (result == 0) { // cancel
            this.emit("canceled");
        }
        else {
            this._onClickSave();
            this.on("saved", lang.hitch(this, this.close));
        }
    }

    private _onClickReloadList(callback?: Function): void {
        this.table.StandbyStart();

        let entityId = this.getAttributeValue("ID");
        let source = this.getAttributeValue("source");

        if (callback && typeof callback === "function") this.on("listReloaded", lang.hitch(this, callback));

        this.RequestManager.callfunction("Webtool_API_Screenplay__Endpoint_LoadListValues", { entityId: entityId }, lang.hitch(this, this._onReloadListResponse), lang.hitch(this, this._onReloadListError));
    }

    private _onReloadListResponse(response: Array<HtEndpointScenario>) {
        this.table.StandbyStop();

        this.store.setup(response);
        this._resetEmbeddedEditor();
        this._setExpanderMarking();
        this._setNewButtonState();
        this.setAttributeValue("deletedDiffData", null);
        this.setAttributeValue("changedDiffData", null);

        this.emit("listReloaded");
        this.table.updateTable();
    }

    private _onReloadListError(error) {
        this.table.StandbyStop();
        console.error(error);
    }

    private _resetEmbeddedEditor(): void {
        if (this.embeddedEditor) {
            this.embeddedEditor.RequestManager.cancelRunningRequests();
            this.embeddedEditor.destroy();
        }
        this.UiModder.domConstruct.empty(this.embeddedEditorContainer);
        this.embeddedEditor = null;
        this.hideNode(this.embeddedEditorContainer, true);
    }

    setup(tabContainer?: MdsLax<InstanceType<typeof TabContainer>>, entity?: Endpoint) {
        let heading;

        this.setupTable();

        if (entity) {
            this.internalName = "endpointEditor_" + entity.ID;
            if (entity.is_new) {
                this.setData(entity);
                heading = this.translate("ScreenPlay_Heading_NewEndpoint");
            } else {
                heading = `${this.translate("Screenplay_Heading_Endpoint")}: ${entity.description}`;
                entity.is_new = false;
                this.setStartupState("ready");
                this._loadEntity(entity.ID);
            }

        } else {
            this.internalName = "endpointEditor_New";
            let newEntity: Scenario = {
                ID: "",
                $Displayname: "",
                Classname: "",
                ClassPrintname: "",
                description: "",
                source: "",
                is_new: true,
                EL_ht_screenplay_scenario_block: [],
                EL_ht_screenplay_scenario_ext_urls: []
            }
            this.setData(newEntity);
            heading = this.translate("Screenplay_Heading_NewEndpoint")
        }

        if (tabContainer) {
            this.show("tab", heading, tabContainer.name);
        } else {
            this.show("dlg", heading, tabContainer.name);
        }

        this.createDefaultButton("save", lang.hitch(this, this._onClickSave), false, "primary");
    }

    private _loadEntity(entityId: string): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Endpoint_LoadEntity",
            { ID: entityId },
            lang.hitch(this, this._onLoadEntityResponse),
            lang.hitch(this, this._onLoadEntityError));
    }

    private _onLoadEntityResponse(response: Scenario): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        response.is_new = false;
        this.setData(response);
    }

    private _onLoadEntityError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _onClickSave(): void {
        if (this.embeddedEditor && this.embeddedEditor.isDirty()) {
            this._closeEditor(null, null, lang.hitch(this, this._onClickSave));
            return;
        }

        this.StandbyStart();
        this._deactivateDataButtons(true, true);



        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        let isNew = this.getAttributeValue("is_new");
        let changedData = this.getChangedData();
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Endpoint_SaveEntity",
            { ID: entityId, Classname: classname, is_new: isNew, changedData: changedData },
            lang.hitch(this, this._onSaveResponse),
            lang.hitch(this, this._onSaveError));
    }

    private _onSaveResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.emit("saved");
        this._loadEntity(response.entityId);
    }

    private _onSaveError(error: more.requestErrorMessage): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        this.showToast(error?.Message, "fas fa-warning-triangle");
    }

    private _onClickRollback(e): void {
        this.rollback();
        this._resetEmbeddedEditor();
    }

    private _onClickReload(e): void {
        let entity = this.getAttributeValue("___root");
        this._loadEntity(entity.ID);
    }

    private _onClickDelete(e): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Endpoint_DeleteEntity",
            { ID: entityId, Classname: classname },
            lang.hitch(this, this._onDeleteResponse),
            lang.hitch(this, this._onDeleteError));
    }

    private _onDeleteResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.close();
    }

    private _onDeleteError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _deactivateDataButtons(deactivate: boolean, deactivateAll?: boolean): void {
        this.requestContextButtonDisable("save", deactivate);
        this.requestContextButtonDisable("rollback", deactivate);
        if (deactivateAll) {
            this.requestContextButtonDisable("delete", deactivate);
            this.requestContextButtonDisable("reload", deactivate);
        }
    }

    protected onDirtyChange(newState: boolean) {
        this._deactivateDataButtons(!newState);
        this.setDirtyMarker(newState);
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        this._resetEmbeddedEditor();
        this._resetSelectedEntities();

        let isNew = this.getAttributeValue("is_new");
        if (!isNew) {
            this.hideNode(this.sectionTableContainer, false);
            this.hideNode(this.tableContainer, false);

            this.createDefaultButton("delete", lang.hitch(this, this._onClickDelete), true, "secondary");
            this.createDefaultButton("rollback", lang.hitch(this, this._onClickRollback), false, "secondary");
            this.createDefaultButton("reload", lang.hitch(this, this._onClickReload), true, "primary");
            this.changeInternalName("endpointEditor_" + this.getAttributeValue("ID"));
            this.setTitles(`${this.translate("Screenplay_Heading_Endpoint")}: ${this.getAttributeValue("description")}`);
        } else {

        }
        this._deactivateDataButtons(true);

        let listValues = this.getAttributeValue("EL_ht_screenplay_endpoint_scenarios");
        this.store.setup(listValues);
        this._setExpanderMarking();
        this._setNewButtonState();


        this.setAttributeValue("changedDiffData", null);
        this.setAttributeValue("deletedDiffData", null);
    }

    protected _onChanged(attribute: string, value: any) {
        switch (attribute) {
            case "date_start":
            case "date_end":
                break;
            case "override_date":
                let newOverrideDate: Date = value;
                if (!newOverrideDate || !newOverrideDate.getTimezoneOffset) {
                    this.setAttributeValue("override_date", null);
                    this.setAttributeValue("override_date_offset", null);
                    return;
                }
                 
                let offset = -newOverrideDate.getTimezoneOffset();
                this.setAttributeValue("override_date_offset", offset);
                break;
        }
    }

    private _openSED(mode: string, evt: MouseEvent): void {
        this.loadSelectEntityDialog(evt, mode, lang.hitch(this, this._onSEDCallback));
    }

    private _onSEDCallback(mode: string, data: Array<any>, items: Array<SelectEntityDialogTypes.Item<any>>) {
        switch (mode) {
            case "selectScenario":
           
                if (items && items.length > 0) {
                    let entity = items[0].entry.data;
                    entity.is_new = false;
                    let htEntity = this._createNewHtEntity(entity);
                    this._embedEditor(htEntity, true);
                }
                break;
            case "selectConfiguration":
                if (items && items.length > 0) {
                    let entity = items[0].entry.data;
                    this.inputConfiguration.setValue(entity);
                }
                break;
            case "selectFallbackTemplate":
                if (items && items.length > 0) {
                    let entity = items[0].entry.data;
                    this.inputFallbackTemplate.setValue(entity);
                }
                break;
        }
    }

    private _onClickNew(evt: MouseEvent) {
        let floatingmenu = new FloatingMenu();
        floatingmenu.addEntry({
            callback: lang.hitch(this, this._onClickNewListEntity),
            label: this.translate("Endpoint_Label_NewEntityButton"),
            enabled: true,
            icon: "fas fa-plus",
            name: "newEntity"
        });
        floatingmenu.addEntry({
            callback: lang.hitch(this, this._openSED, "selectScenario", evt),
            label: this.translate("Endpoint_Label_AddScenarioButton"),
            enabled: true,
            icon: "fas fa-plus",
            name: "addScenario"
        });
        floatingmenu.open(evt);
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.SEDconfigCallfunction = "Webtool_API_Screenplay__Endpoint_GetSEDConfig";
        this.inputConfiguration.addInlineButton(lang.hitch(this, this._openSED, "selectConfiguration"));
        this.inputFallbackTemplate.addInlineButton(lang.hitch(this, this._openSED, "selectFallbackTemplate"));

    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(EndpointEditor);